import i18n from "@/i18n";
import store from "@/store/index.js";

export default class CrudStoreBuilder {
  service = undefined;
  dependents = undefined;
  converter = undefined;
  state = {};
  getters = {};
  mutations = {};
  actions = {};

  setName(name) {
    this.name = name;
    this._names = `${name}s`;
    this._Name = `${name[0].toUpperCase()}${name.substring(1)}`;
    this._Names = `${this._Name}s`;
    this._NAME = name.toUpperCase();
    this._NAMES = `${this._NAME}S`;

    return this;
  }

  setService(service) {
    this.service = service;
    return this;
  }

  setDependents(dependents) {
    this.dependents = dependents;
    return this;
  }

  setConverter(converter) {
    this.converter = converter;
    return this;
  }

  includeState(state) {
    this.state = state;
    return this;
  }

  includeActions(actions) {
    this.actions = actions;
    return this;
  }

  includeMutations(mutations) {
    this.mutations = mutations;
    return this;
  }

  includeGetters(getters) {
    this.getters = getters;
    return this;
  }

  #buildState() {
    let state = {};

    state[`${this._names}`] = [];
    state.fetched = false;
    state.enableNotifs = true;

    return { ...state, ...this.state };
  }

  #buildGetters() {
    let getters = {};

    getters[`get${this._Names}`] = (state) => {
      return state[`${this._names}`];
    };

    getters[`get${this._Name}ById`] = (state) => (id) => {
      return state[this._names].find((x) => x.id === id);
    };

    getters[`was${this._Names}Fetched`] = (state) => {
      return state.fetched;
    };

    return { ...getters, ...this.getters };
  }

  #buildMutations() {
    let mutations = {};

    mutations[`SET_${this._NAMES}`] = (state, v) => {
      state[this._names] = v;
    };

    mutations[`ADD_NEW_${this._NAME}`] = (state, v) => {
      state[this._names].push(v);
    };

    mutations[`EDIT_${this._NAME}`] = (state, v) => {
      const index = state[this._names].findIndex((x) => x.id === v.id);
      if (index !== -1) state[this._names].splice(index, 1, v);
    };

    mutations[`REMOVE_${this._NAME}`] = (state, v) => {
      const index = state[this._names].findIndex((x) => x.id === v);
      if (index !== -1) state[this._names].splice(index, 1);
    };

    mutations["SET_FETCHED"] = (state, v) => {
      state.fetched = v;
    };

    mutations[`ON_BEFORE_LOAD_${this._NAMES}`] = () => {};
    mutations[`ON_LOAD_NEW_${this._NAME}`] = () => {};

    mutations["ENABLE_NOTIFICATIONS"] = (state, enable) => {
      state.enableNotifs = enable;
    };

    mutations["SHOW_NOTIFICATION"] = (state, v) => {
      if (!state.enableNotifs) return;
      v.dispatch(
        "notificationModule/showNotification",
        {
          messageAlert: v.message,
          typeAlert: v.type,
        },
        {
          root: true,
        }
      );
    };

    return { ...mutations, ...this.mutations };
  }

  #showSuccess({ commit, dispatch }, message) {
    commit("SHOW_NOTIFICATION", {
      dispatch: dispatch,
      message: i18n.t(message),
      type: "success",
    });
  }
  #showError(error, { commit, dispatch }, message) {
    const localized = i18n.t(message);
    commit("SHOW_NOTIFICATION", {
      dispatch: dispatch,
      message: localized,
      type: "error",
    });
    throw new Error(localized, { cause: error });
  }

  #buildActions() {
    let actions = {};

    let actionTemplate = (apply, resolve, reject) => {
      return async ({ commit, dispatch }, entity) => {
        await apply(entity)
          .then((response) => {
            resolve(response, { commit, dispatch }, entity);
          })
          .catch((error) => {
            console.log("error", error);
            reject(error, { commit, dispatch });
          });
      };
    };

    actions[`load${this._Names}`] = actionTemplate(
      () => {
        return this.service.getAll();
      },
      (response, { commit, dispatch }) => {
        const a = [];
        commit(`ON_BEFORE_LOAD_${this._NAMES}`);
        response.data.forEach((x) => {
          dispatch(`load${this._Name}`, x).then((x) => {
            a.push(x);
            commit(`ON_LOAD_NEW_${this._NAME}`, x);
          });
        });
        commit(`SET_${this._NAMES}`, a);
      },
      (error, { commit, dispatch }) => {
        this.#showError(
          error,
          { commit, dispatch },
          `messageStores.${this.name}AllError`
        );
      }
    );

    actions[`add${this._Name}`] = actionTemplate(
      (entity) => {
        if (this.converter) this.converter(entity);
        return this.service.save(entity);
      },
      (response, { commit, dispatch }) => {
        dispatch(`load${this._Name}`, response.data).then((x) => {
          commit(`ADD_NEW_${this._NAME}`, x);
        });
        this.#showSuccess(
          { commit, dispatch },
          `messageStores.${this.name}AddSuccess`
        );
      },
      (error, { commit, dispatch }) => {
        this.#showError(
          error,
          { commit, dispatch },
          `messageStores.${this.name}AddError`
        );
      }
    );

    actions[`edit${this._Name}`] = actionTemplate(
      (entity) => {
        if (this.converter) this.converter(entity);
        return this.service.update(entity);
      },
      (response, { commit, dispatch }, entity) => {
        dispatch(`load${this._Name}`, response.data).then((x) => {
          commit(`EDIT_${this._NAME}`, x);
        });
        this.#showSuccess(
          { commit, dispatch },
          `messageStores.${this.name}EditSuccess`
        );
      },
      (error, { commit, dispatch }) => {
        this.#showError(
          error,
          { commit, dispatch },
          `messageStores.${this.name}EditError`
        );
      }
    );

    actions[`delete${this._Name}`] = actionTemplate(
      (entity) => {
        return this.service.deleteById(entity.id);
      },
      (response, { commit, dispatch }, entity) => {
        if (response.status === 200 || response.status === 202) {
          commit(`REMOVE_${this._NAME}`, entity.id);
          this.#showSuccess(
            { commit, dispatch },
            `messageStores.${this.name}DeleteSuccess`
          );
        }
      },
      (error, { commit, dispatch }) => {
        this.#showError(
          error,
          { commit, dispatch },
          `messageStores.${this.name}DeleteError`
        );
      }
    );

    actions[`load${this._Names}WithDependents`] = async ({
      commit,
      dispatch,
    }) => {
      const thisGetterName = `${this.name}Module/get${this._Names}`;
      const thisLoaderName = `${this.name}Module/load${this._Names}`;
      const thisElems = store.getters[thisGetterName];

      if (!thisElems || thisElems.length === 0) {
        if (this.dependents) {
          for (const i of this.dependents) {
            const moduleName = `${i}Module`;
            const depName = `${i[0].toUpperCase()}${i.substring(1)}`;
            const getterName = `${moduleName}/get${depName}s`;
            const loaderExName = `${moduleName}/load${depName}sWithDependents`;
            const loaderName = store._actions[loaderExName]
              ? loaderExName
              : `${moduleName}/load${depName}s`;
            const elems = store.getters[getterName];

            if (!elems || elems.length === 0)
              await store.dispatch(loaderName, { commit, dispatch });
          }

          await store.dispatch(thisLoaderName, { commit, dispatch });
          await commit("SET_FETCHED", true);
        }
      }
    };

    actions[`show${this._Name}StoreNotifications`] = ({ commit }, enable) => {
      commit("ENABLE_NOTIFICATIONS", enable);
    };

    return { ...actions, ...this.actions };
  }

  build() {
    const state = this.#buildState();
    const getters = this.#buildGetters();
    const mutations = this.#buildMutations();
    const actions = this.#buildActions();

    return {
      namespaced: true,
      state,
      getters,
      mutations,
      actions,
    };
  }
}
